/**
 * Funções utilitárias para validação de objetos e dimensões
 *
 * Motor unificado de validação:
 * - `validateOrderDimensions` valida largura/altura por objeto (dura, sempre ativa).
 * - `validateOrderMeterage` calcula a comparação metragem vendida vs calculada.
 * - `getValidationPolicy` define, por fluxo, se a comparação de metragem bloqueia.
 * - `validateOrder` combina dimensão + metragem de acordo com a policy do fluxo.
 *
 * Funções legadas (`validateObjectDimensions`, `checkSaveErrors`) são mantidas como
 * aliases finos para preservar compatibilidade com consumers ainda não migrados.
 */

import { OrderObject, ProductRules } from '../types/objects';
import { debugLog } from '../../../lib/terminalLogger';

/**
 * Fluxos de salvamento suportados pelo motor de validação.
 * - `official-save`: salvamento oficial (compara metragem vendida vs calculada).
 * - `temp-save`: salvamento temporário (NÃO compara metragem vendida vs calculada).
 * - `quotation`: orçamento (NÃO compara metragem vendida vs calculada).
 */
export type ValidationMode = 'official-save' | 'temp-save' | 'quotation';

/**
 * Policy explícita por fluxo de validação.
 */
export interface ValidationPolicy {
  /** Dimensão individual (largura/altura) sempre bloqueia em todos os fluxos. */
  blockOnDimension: boolean;
  /** Comparação metragem vendida vs calculada bloqueia apenas em fluxos oficiais. */
  blockOnMeterage: boolean;
}

/**
 * Retorna a policy de validação para um determinado fluxo.
 *
 * Regra de negócio:
 * - dimensão individual sempre bloqueia;
 * - comparação vendida vs calculada bloqueia somente no fluxo oficial.
 */
export const getValidationPolicy = (
  mode: ValidationMode,
  opts?: { validaDimensao?: boolean; validaMetragem?: boolean },
): ValidationPolicy => {
  const base: ValidationPolicy = (() => {
    switch (mode) {
      case 'official-save':
        return { blockOnDimension: true, blockOnMeterage: true };
      case 'temp-save':
      case 'quotation':
        return { blockOnDimension: true, blockOnMeterage: false };
      default:
        return { blockOnDimension: true, blockOnMeterage: false };
    }
  })();

  return {
    blockOnDimension: base.blockOnDimension && opts?.validaDimensao !== false,
    blockOnMeterage: base.blockOnMeterage && opts?.validaMetragem !== false,
  };
};

export interface OrderMeterageInput {
  totalAreaWithQuantity: number;
  metrosVendidos: number;
  isMetros: boolean;
}

export interface OrderMeterageResult {
  hasExceededMeterage: boolean;
  metrageExcess: number;
}

/**
 * Calcula a comparação entre metragem vendida e calculada.
 * Não decide bloqueio: apenas reporta excesso. Quem decide é a policy do fluxo.
 */
export const validateOrderMeterage = ({
  totalAreaWithQuantity,
  metrosVendidos,
  isMetros,
}: OrderMeterageInput): OrderMeterageResult => {
  const hasExceededMeterage = metrosVendidos > 0 && totalAreaWithQuantity > metrosVendidos;

  if (!hasExceededMeterage) {
    return { hasExceededMeterage: false, metrageExcess: 0 };
  }

  const metrageExcess = isMetros
    ? Math.ceil((totalAreaWithQuantity - metrosVendidos) * 100) / 100
    : Math.round(totalAreaWithQuantity - metrosVendidos);

  return { hasExceededMeterage: true, metrageExcess };
};

export interface OrderValidationInput {
  selectedObjects: OrderObject[];
  productRules?: ProductRules;
  metrosVendidos: number;
  totalAreaWithQuantity: number;
  isMetros: boolean;
  mode?: ValidationMode;
}

export interface OrderValidationResult {
  validatedObjects: OrderObject[];
  hasErrors: boolean;
  hasExceededMeterage: boolean;
  metrageExcess: number;
  /**
   * Bloqueio efetivo de salvamento conforme a policy do fluxo:
   * dimensão sempre conta; metragem só conta nos fluxos oficiais.
   */
  hasSaveErrors: boolean;
}

/**
 * Motor único de validação. Combina dimensão (sempre dura) com a comparação de
 * metragem conforme a policy do fluxo informado.
 */
export const validateOrder = ({
  selectedObjects,
  productRules,
  metrosVendidos,
  totalAreaWithQuantity,
  isMetros,
  mode = 'official-save',
}: OrderValidationInput): OrderValidationResult => {
  const validaDimensao = (productRules as any)?.valida_dimensao;
  const validaMetragem = (productRules as any)?.valida_metragem;
  const policy = getValidationPolicy(mode, { validaDimensao, validaMetragem });

  const validatedObjects = validaDimensao === false
    ? selectedObjects.map(o => ({ ...o, status: 'success' as const }))
    : validateOrderDimensions(selectedObjects, productRules);

  const hasErrors = validatedObjects.some(obj => obj.status === 'error');
  const { hasExceededMeterage, metrageExcess } = validateOrderMeterage({
    totalAreaWithQuantity,
    metrosVendidos,
    isMetros,
  });

  const hasSaveErrors =
    (policy.blockOnDimension && hasErrors) ||
    (policy.blockOnMeterage && hasExceededMeterage);

  return {
    validatedObjects,
    hasErrors,
    hasExceededMeterage,
    metrageExcess,
    hasSaveErrors,
  };
};

/**
 * Valida as dimensões dos objetos com base nas regras do produto
 * @param objects Lista de objetos para validar
 * @param productRules Regras do produto
 * @returns Lista de objetos com status de validação atualizado
 */
export const validateOrderDimensions = (
  objects: OrderObject[], 
  productRules?: ProductRules
): OrderObject[] => {
  if (!productRules) {
    debugLog('ACTION: validate-dimensions <- received', { 
      status: 'warning', 
      data: { 
        mensagem: 'Sem regras disponíveis para validação de dimensões' 
      } 
    });
    return objects;
  }

  const { largura_maxima_cm, altura_maxima_cm } = productRules;

  // ✅ BYPASS: null/undefined OU <= 0 significam "sem limite de dimensões".
  // NULL é o valor canônico no banco; <= 0 é blindagem contra cadastro errado no admin.
  // Isso permite categorias como CAMISETA que não têm limites de dimensão
  if (
    largura_maxima_cm == null ||
    altura_maxima_cm == null ||
    largura_maxima_cm <= 0 ||
    altura_maxima_cm <= 0
  ) {
    debugLog('ACTION: validate-dimensions <- bypass', { 
      status: 'info', 
      data: { 
        mensagem: 'Dimensões máximas null - bypass de validação',
        categoria: productRules.nome_servico,
        largura_maxima_cm,
        altura_maxima_cm
      } 
    });
    // Retornar objetos com status 'success' sem validar dimensões
    return objects.map(obj => ({ 
      ...obj, 
      status: 'success' as const, 
      message: 'Sem limite de dimensões para esta categoria' 
    }));
  }

  debugLog('ACTION: validate-dimensions -> sending', { 
    method: 'validar-dimensoes', 
    data: { 
      totalObjetos: objects.length,
      larguraMaxima: largura_maxima_cm,
      alturaMaxima: altura_maxima_cm
    } 
  });

  const validatedObjects = objects.map(obj => {
    const validatedObj = { ...obj };
    const widthPercentage = (obj.width / largura_maxima_cm) * 100;
    const heightPercentage = (obj.height / altura_maxima_cm) * 100;

    // Verificação de largura excedida
    const larguraExcedida = obj.width > largura_maxima_cm;
    // Verificação de altura excedida
    const alturaExcedida = obj.height > altura_maxima_cm;

    if (larguraExcedida || alturaExcedida) {
      validatedObj.status = 'error';
      validatedObj.message = `Dimensões excedem o máximo permitido (${largura_maxima_cm}cm x ${altura_maxima_cm}cm)`;
      
      // 🔍 LOG DETALHADO DE OBJETO COM ERRO
      try {
        debugLog('🚨 [VALIDATOR] OBJETO EXCEDE DIMENSÕES', {
          id: obj.id,
          nome: obj.nome,
          dimensoesObjeto: { width: obj.width, height: obj.height },
          dimensoesMaximas: { largura_maxima_cm, altura_maxima_cm },
          larguraExcedida,
          alturaExcedida,
          excedeLarguraEm: larguraExcedida ? obj.width - largura_maxima_cm : 0,
          excedeAlturaEm: alturaExcedida ? obj.height - altura_maxima_cm : 0
        });
      } catch {}
    } else if (obj.width < largura_maxima_cm * 0.15) {
      validatedObj.status = 'warning';
      validatedObj.message = `Largura muito pequena (${obj.width}cm - ${widthPercentage.toFixed(1)}% do máximo)`;
    } else {
      validatedObj.status = 'success';
      validatedObj.message = 'Dimensões adequadas para impressão';
    }

    return validatedObj;
  });

  const statusCount = {
    success: validatedObjects.filter(obj => obj.status === 'success').length,
    warning: validatedObjects.filter(obj => obj.status === 'warning').length,
    error: validatedObjects.filter(obj => obj.status === 'error').length
  };

  debugLog('ACTION: validate-dimensions <- received', { 
    status: 'ok', 
    data: { 
      totalValidados: validatedObjects.length,
      statusCount
    } 
  });

  return validatedObjects;
};

/**
 * Verifica se há erros que impedem o salvamento
 * @param objects Lista de objetos para verificar
 * @param totalAreaWithQuantity Área total calculada
 * @param metrosVendidos Metros vendidos para comparação
 * @returns Objeto indicando se há erros e detalhes
 */
export const validateObjectDimensions = validateOrderDimensions;

/**
 * Verifica se há erros que impedem o salvamento
 * @param objects Lista de objetos para verificar
 * @param totalAreaWithQuantity Área total calculada
 * @param metrosVendidos Metros vendidos para comparação
 * @returns Objeto indicando se há erros e detalhes
 */
export const checkSaveErrors = (
  objects: OrderObject[],
  totalAreaWithQuantity: number,
  metrosVendidos: number,
  mode: ValidationMode = 'official-save',
  isMetros = true,
) => {
  const policy = getValidationPolicy(mode);
  const hasErrors = objects.some(obj => obj.status === 'error');
  const { hasExceededMeterage } = validateOrderMeterage({
    totalAreaWithQuantity,
    metrosVendidos,
    isMetros,
  });
  const hasSaveErrors =
    (policy.blockOnDimension && hasErrors) ||
    (policy.blockOnMeterage && hasExceededMeterage);

  // 🔍 LOG DETALHADO DE VALIDAÇÃO
  try {
    const objectsWithErrors = objects.filter(obj => obj.status === 'error');
    debugLog('🔍 [VALIDATOR] checkSaveErrors', {
      totalObjects: objects.length,
      objectsWithErrors: objectsWithErrors.length,
      objectsWithErrorsDetails: objectsWithErrors.map(obj => ({
        id: obj.id,
        nome: obj.nome,
        width: obj.width,
        height: obj.height,
        status: obj.status,
        message: obj.message
      })),
      totalAreaWithQuantity,
      metrosVendidos,
      metrosVendidosPositivo: metrosVendidos > 0,
      excede: totalAreaWithQuantity > metrosVendidos,
      diferenca: totalAreaWithQuantity - metrosVendidos,
      hasErrors,
      hasExceededMeterage,
      hasSaveErrors
    });
  } catch {}

  return {
    hasErrors,
    hasExceededMeterage,
    hasSaveErrors
  };
}; 

/**
 * Função de teste para verificar se a validação de altura está funcionando
 */
export const testValidation = () => {
  debugLog('ACTION: test-validation -> sending', { 
    method: 'teste-validacao', 
    data: { 
      tipo: 'validacao-dimensoes' 
    } 
  });
  
  const testRules: ProductRules = {
    nome_servico: 'Teste',
    descricao: 'Teste de validação',
    horario_corte: '18:00',
    largura_maxima_cm: 100,
    altura_maxima_cm: 150,
    exportacao: {
      formato: 'PDF',
      resolucao_dpi: 300
    }
  };

  const testObjects: OrderObject[] = [
    {
      id: 'test-1',
      nome: 'Objeto Normal',
      width: 80,
      height: 120,
      quantity: 1,
      status: 'success'
    },
    {
      id: 'test-2',
      nome: 'Objeto Largura Excedida',
      width: 120, // Excede largura máxima (100cm)
      height: 80,
      quantity: 1,
      status: 'success'
    },
    {
      id: 'test-3',
      nome: 'Objeto Altura Excedida',
      width: 80,
      height: 180, // Excede altura máxima (150cm)
      quantity: 1,
      status: 'success'
    },
    {
      id: 'test-4',
      nome: 'Objeto Ambas Excedidas',
      width: 120, // Excede largura
      height: 180, // Excede altura
      quantity: 1,
      status: 'success'
    }
  ];

  const validatedObjects = validateObjectDimensions(testObjects, testRules);
  
  debugLog('ACTION: test-validation <- received', { 
    status: 'ok', 
    data: { 
      totalTestados: validatedObjects.length,
      resultados: validatedObjects.map(obj => ({
        nome: obj.nome,
        status: obj.status,
        message: obj.message
      }))
    } 
  });
  
  return validatedObjects;
}; 